import React, { useState, useEffect, useContext } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Toolbar,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import StoreIcon from "@mui/icons-material/Store";
import BusinessIcon from "@mui/icons-material/Business";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext"; // Adjust as per your project
import AccountCircle from "@mui/icons-material/AccountCircle";

const navItems = [
  // {
  //   label: "Dashboard",
  //   icon: <DashboardIcon />,
  //   path: "/",
  //   roles: ["super_admin", "admin", "branch_manager"],
  // },
  {
    label: "Account",
    icon: <AccountCircle />,
    roles: ["super_admin", "admin", "branch_manager"],
    children: [
      {
        label: "Reset Password",
        path: "/reset-password",
        roles: ["super_admin", "admin", "branch_manager"],
      },
    ],
  },
  {
    label: "Administration",
    icon: <BusinessIcon />,
    roles: ["super_admin", "admin", "branch_manager"],
    children: [
      {
        label: "Organizations",
        path: "/orgs",
        roles: ["super_admin", "admin"],
      },
      {
        label: "Academic Years",
        path: "/academic-years",
        roles: ["super_admin", "admin"],
      },
      {
        label: "Org Settings",
        path: "/preferences",
        roles: ["super_admin", "admin"],
      },
      {
        label: "Grades & Programs",
        path: "/org-data",
        roles: ["super_admin", "admin"],
      },
      {
        label: "Branches",
        path: "/branches",
        roles: ["super_admin", "admin"],
      },
      {
        label: "Users",
        path: "/users",
        roles: ["super_admin", "admin"],
      },
      {
        label: "Students",
        path: "/students",
        roles: ["super_admin", "admin", "branch_manager"],
      },
    ],
  },
  {
    label: "Procurement",
    icon: <StoreIcon />,
    roles: ["super_admin", "admin"],
    children: [
      { label: "Vendors", path: "/vendors", roles: ["super_admin", "admin"] },
      {
        label: "Book Catalog",
        path: "/catalog/books",
        roles: ["super_admin", "admin"],
      },
      {
        label: "Uniform Catalog",
        path: "/catalog/uniforms",
        roles: ["super_admin", "admin"],
      },
      {
        label: "Stock Requests",
        path: "/procurement/requests",
        roles: ["super_admin", "admin"],
      },
      {
        label: "Stock Received",
        path: "/procurement/receipts",
        roles: ["super_admin", "admin"],
      },
      {
        label: "Vendor Payments",
        path: "/procurement/payments",
        roles: ["super_admin", "admin"],
      },
    ],
  },
  {
    label: "Inventory",
    icon: <StoreIcon />,
    roles: ["super_admin", "admin", "branch_manager"],
    children: [
      {
        label: "Current Stock",
        path: "/inventory/dashboard",
        roles: ["super_admin", "admin", "branch_manager"],
      },
      {
        label: "Stock Upload",
        path: "/inventory/upload",
        roles: ["super_admin", "admin"],
      },
      {
        label: "Stock Transfers",
        path: "/inventory/transfer",
        roles: ["super_admin", "admin", "branch_manager"],
      },
      {
        label: "Teacher Sets",
        path: "/inventory/teacher-sets",
        roles: ["super_admin", "admin"],
      },
    ],
  },
  {
    label: "Store Sales",
    icon: <ShoppingCartIcon />,
    roles: ["super_admin", "admin", "branch_manager"],
    children: [
      {
        label: "Student Purchases (Books)",
        path: "/sales",
        roles: ["super_admin", "admin", "branch_manager"],
      },
      {
        label: "Uniform Sales",
        path: "/sales/uniforms",
        roles: ["super_admin", "admin", "branch_manager"],
      },
      {
        label: "Student Book Mapping",
        path: "/sales/item-mappings",
        roles: ["super_admin", "admin"],
      },
    ],
  },
  {
    label: "Reports",
    icon: <AssessmentIcon />,
    path: "/reports",
    roles: ["super_admin", "admin"],
    children: [
      {
        label: "Sales Dashboard",
        path: "/sales/dashboard",
        roles: ["super_admin", "admin"],
      },
      {
        label: "Student Purchase Coverage",
        path: "/sales/student/dashboard",
        roles: ["super_admin", "admin"],
      },
      {
        label: "Sale Item Reports (Books)",
        path: "/sales/item/dashboard",
        roles: ["super_admin", "admin"],
      },
    ],
  },
];

const Sidebar = () => {
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [openMenus, setOpenMenus] = useState({});

  useEffect(() => {
    const initialOpen = {};
    filteredNavItems.forEach((item) => {
      if (item.children) {
        if (
          item.children.some((child) =>
            location.pathname.startsWith(child.path)
          )
        ) {
          initialOpen[item.label] = true;
        }
      }
    });
    setOpenMenus(initialOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleToggle = (label) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isActive = (path) => location.pathname === path;

  const filterNavItemsForRole = (items, role) => {
    return items
      .filter((item) => item.roles.includes(role))
      .map((item) => {
        if (item.children) {
          const filteredChildren = item.children.filter((child) =>
            child.roles.includes(role)
          );
          return filteredChildren.length > 0
            ? { ...item, children: filteredChildren }
            : null;
        }
        return item;
      })
      .filter(Boolean);
  };

  const filteredNavItems = filterNavItemsForRole(navItems, user.role);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: 220,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: { width: 220, boxSizing: "border-box" },
      }}
    >
      <Toolbar />
      <List>
        {filteredNavItems.map((item) => (
          <React.Fragment key={item.label}>
            {item.children ? (
              <>
                <ListItemButton onClick={() => handleToggle(item.label)}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                  {openMenus[item.label] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse
                  in={openMenus[item.label]}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {item.children.map((child) => (
                      <ListItemButton
                        key={child.label}
                        component={Link}
                        to={child.path}
                        selected={isActive(child.path)}
                        sx={{ pl: 4 }}
                      >
                        <ListItemText primary={child.label} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              <ListItemButton
                component={Link}
                to={item.path}
                selected={isActive(item.path)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            )}
          </React.Fragment>
        ))}
      </List>
    </Drawer>
  );
};

export default Sidebar;
